import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Zap, Radio, Users, KeyRound, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { timeAgo, fmtDateTime } from "@/lib/format";
import { SourceBadge, CodeCell, Empty } from "@/components/bits";
import { cn } from "@/lib/utils";

function Stat({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="eyebrow">{label}</p>
        <Icon className="h-3.5 w-3.5 text-zinc-600" />
      </div>
      <p className="mt-2 font-display text-2xl font-bold text-zinc-100">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

export default function Codes() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const codes = useQuery({
    queryKey: ["codes", q],
    queryFn: () => api.get("/codes", { params: { q, limit: 100 } }).then((r) => r.data),
    refetchInterval: 5000, staleTime: 0,
  });
  const stats = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/stats").then((r) => r.data), refetchInterval: 10000, staleTime: 0 });
  const summary = useQuery({ queryKey: ["sources-summary"], queryFn: () => api.get("/sources/summary").then((r) => r.data), refetchInterval: 15000, staleTime: 0 });

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post("/codes/test", {});
      toast.success(`Test code ${r.data.code} added — open the test page or any code field to fill it.`);
      qc.invalidateQueries({ queryKey: ["codes"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const s = stats.data;
  const sm = summary.data;
  const items = codes.data?.items || [];
  const missing = sm && (!sm.openphone_connected || !sm.gmail_mailboxes);

  return (
    <div className="rise flex flex-col gap-6">
      {missing && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="sources-missing-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {!sm.openphone_connected && !sm.gmail_mailboxes
                ? "No sources connected yet — texted and emailed codes can't appear until OpenPhone or a Gmail inbox is connected."
                : !sm.gmail_mailboxes
                  ? "No Gmail inbox connected — emailed verification codes won't appear here or in the extension."
                  : "OpenPhone isn't connected — texted codes won't appear here or in the extension."}
            </span>
          </div>
          {user?.is_admin ? (
            <Link to="/sources" className="font-semibold underline underline-offset-2 hover:text-amber-100" data-testid="sources-missing-link">Connect on Sources →</Link>
          ) : <span className="text-amber-300/80">Ask your admin to connect it.</span>}
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">Codes</h1>
          <p className="mt-1 text-sm text-zinc-400">Every verification code that reached your connected sources. Codes under 10 minutes old are offered by the extension.</p>
        </div>
        {user?.is_admin && (
          <Button onClick={sendTest} disabled={sending} data-testid="send-test-code-button" variant="secondary" className="gap-2">
            <Zap className="h-4 w-4" /> {sending ? "Sending…" : "Send test code"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Radio} label="Sources" value={s ? s.sources_connected : "—"} hint={s?.sources_attention ? `${s.sources_attention} need attention` : "connected"} testId="stat-sources" />
        <Stat icon={KeyRound} label="Codes today" value={s ? s.codes_today : "—"} hint={s ? `${s.codes_total} total` : ""} testId="stat-codes-today" />
        <Stat icon={Users} label="Members" value={s ? s.members : "—"} hint="can sign in" testId="stat-members" />
        <Stat icon={Clock} label="Latest code" value={s?.latest_code_age != null ? timeAgo(s.latest_code_age) : "—"} hint={s?.latest_code_age != null && s.latest_code_age <= 600 ? "fresh · fillable" : "none fresh"} testId="stat-latest" />
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, sender, subject, brand…" className="bg-[#0B0D11] pl-9" data-testid="codes-search-input" />
      </div>

      {codes.isLoading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : items.length === 0 ? (
        <Empty title={q ? "No codes match" : "No codes yet"} testId="codes-empty">
          {q ? "Try a different search." : user?.is_admin ? "Connect OpenPhone or a Gmail inbox on the Sources page, or press “Send test code” to see how it works." : "Codes will appear here as soon as they reach a connected source."}
        </Empty>
      ) : (
        <div className="surface overflow-x-auto">
          <table className="table-dense w-full" aria-label="Codes" data-testid="codes-table">
            <thead className="border-b border-[#242A36]">
              <tr><th>Received</th><th>Source</th><th>From</th><th>Code</th><th>Filled</th></tr>
            </thead>
            <tbody aria-live="polite">
              {items.map((c) => (
                <tr key={c.id} data-testid={`code-row-${c.id}`} className={cn(!c.fresh && "text-zinc-500")}>
                  <td className="whitespace-nowrap">
                    <span className={cn("font-mono text-xs", c.fresh ? "text-emerald-300" : "text-zinc-500")} title={fmtDateTime(c.received_at)}>
                      {c.age_seconds < 30 && <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />}
                      {timeAgo(c.received_at)}
                    </span>
                  </td>
                  <td><SourceBadge type={c.source_type} /><span className="ml-2 font-mono text-xs text-zinc-500">{c.source_label}</span></td>
                  <td className="max-w-xs">
                    <p className="truncate text-zinc-200">{c.sender_name || c.sender}</p>
                    <p className="truncate text-xs text-zinc-500">{c.subject || c.snippet}</p>
                  </td>
                  <td><CodeCell code={c.code} dim={!c.fresh} testId={`copy-code-${c.id}`} /></td>
                  <td className="text-xs">
                    {c.fill_count ? (
                      <span title={c.fills.map((f) => `${f.email} · ${f.host}`).join("\n")} className="text-zinc-300">
                        {c.fill_count}× · {c.fills[c.fills.length - 1].email.split("@")[0]} on {c.fills[c.fills.length - 1].host || "—"}
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-zinc-600">Raw texts and email bodies are kept 7 days; codes and metadata 90 days.</p>
    </div>
  );
}
